import Leaflet from 'leaflet';
import React from 'react';
import { Marker, Popup } from 'react-leaflet';

import { ClickOnceButton } from './ClickOnceButton';
import {
  deviceColor,
  selectColor,
  alarmColor,
  activeColor,
  traceColor,
  beaconColor,
  checkinColor,
  gatewayColor,
  lfDeviceColor,
} from './styles/constants';
import type { Device, DeviceCategory } from './types';
import { showAsTrace, showAsAlarm, showAsActive } from './utils/deviceUtils';

/**
 * Extended device with callback functions for marker interactions
 */
export interface DevicePoint extends Device {
  onSelect?: () => void;
  onAck?: () => void;
  onDis?: () => void;
  onBeginTracking?: () => void;
  onDeactivateTracking?: () => void;
  onCheckin?: () => void;
  onUncheck?: () => void;
  onSetCurrentCheckpoint?: (id: string | null) => void;
  onClearAllCheckins?: (id: string) => void;
  isChecker?: boolean;
  currentCheckpoint?: string | null;
}

/**
 * Solution point for trace markers
 */
export interface SolutionPoint {
  id: string;
  lat: number;
  lng: number;
  timestamp: Date;
  accuracy?: number;
  addedTimestamp?: Date;
  status?: string;
  title?: string;
  text?: string;
}

/**
 * Props for DeviceMarker component
 */
interface DeviceMarkerProps {
  position: [number, number];
  map: Leaflet.Map;
  title: string;
  timestamp: Date;
  opacity: number;
  positionTime: Date;
  alarmTime?: Date;
  checkedTime?: Date;
  color: string;
  accuracy?: number;
  point: DevicePoint;
  selected: boolean;
  zindex: number;
  showAccuracy?: boolean;
  shape: MarkerShape;
  markerWidthRem?: number | null;
  markerHeightRem?: number | null;
}

/**
 * Props for SolutionMarker component
 */
interface SolutionMarkerProps {
  position: [number, number];
  map: Leaflet.Map;
  title?: string;
  opacity: number;
  color: string;
  point: SolutionPoint;
  selected: boolean;
  zoom: number;
  zindex: number;
  showAccuracy?: boolean;
}

const showAsChecked = (device: DevicePoint): boolean => {
  return Boolean(device && device.checkinTime);
};

/**
 * Get device color based on deviceCategory
 */
const getDeviceCategoryColor = (device: DevicePoint): string => {
  if (!device || !device.deviceCategory) {
    return deviceColor; // Default gray for unspecified
  }

  switch (device.deviceCategory) {
    case 'lf_beacon':
      return beaconColor; // Blue
    case 'lf_checkin':
      return checkinColor; // Green
    case 'lf_gateway':
      return gatewayColor; // Purple
    case 'lf_device':
      return lfDeviceColor; // Gray
    default:
      return deviceColor; // Default gray
  }
};

/**
 * Escape a string for safe interpolation into a Leaflet divIcon HTML string.
 * Leaflet assigns the `html` option via innerHTML with no escaping, so device-supplied
 * values (e.g. titles, which can come from external Yggio device names) must be escaped
 * to prevent stored XSS. (H-6)
 */
export const escapeHtml = (s: string): string =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const headerSize = '2vmin';

type MarkerShape = 'pin' | 'square' | 'circle' | 'diamond';

const getMarkerShape = (category: DeviceCategory | undefined): MarkerShape => {
  switch (category) {
    case 'lf_checkin':
      return 'square';
    case 'lf_beacon':
      return 'circle';
    case 'lf_gateway':
      return 'diamond';
    default:
      return 'pin';
  }
};

const shapeStyles: Record<MarkerShape, { borderRadius: string; transform: string }> = {
  pin: { borderRadius: '1rem 1rem 0', transform: 'rotate(45deg)' },
  square: { borderRadius: '0', transform: 'none' },
  circle: { borderRadius: '50%', transform: 'none' },
  diamond: { borderRadius: '0', transform: 'rotate(45deg)' },
};

const DEFAULT_MARKER_SIZE_REM = 1.8;

const makeMarkerHtmlStyles = (
  color: string,
  opacity: number,
  selected: boolean,
  checked = false,
  shape: MarkerShape = 'pin',
  widthRem?: number | null,
  heightRem?: number | null
): string => {
  const { borderRadius, transform } = shapeStyles[shape];
  const w = widthRem ?? DEFAULT_MARKER_SIZE_REM;
  const h = heightRem ?? DEFAULT_MARKER_SIZE_REM;
  return `
  background-color: ${color};
  opacity: ${opacity};
  width: ${w}rem;
  height: ${h}rem;
  display: block;
  left: ${-w / 2}rem;
  top: ${-h / 2}rem;
  position: relative;
  border-radius: ${borderRadius};
  transform: ${transform};
  border: ${checked ? '3px solid #4b4' : '1px solid #000000'}`;
};

const makeCenterHtmlStyles = (bAlarm: boolean, bChecked: boolean, selected: boolean): string => `
  width: ${'.8rem'};
  height: ${'.8rem'};
  display: block;
  left: ${'-.4rem'};
  top: ${'-2.3rem'};
  position: relative;
  border-radius: ${'.5rem .5rem .5rem .5rem'};
  transform: rotate(45deg);
  border: 1px solid #000000`;

const divStyle = `
  width: 10rem;
  position: relative;
  top: -1.5rem;
  color: #880000;
  font-weight: 500;
  text-align: center;
  left: -5rem;
`;

const makeMarkerIcon = (
  name: string,
  color: string,
  opacity: number,
  alarm: boolean,
  checked: boolean,
  selected: boolean,
  shape: MarkerShape = 'pin',
  widthRem?: number | null,
  heightRem?: number | null
): Leaflet.DivIcon => {
  const icon = Leaflet.divIcon({
    className: 'lfndr-custom-pin',
    iconAnchor: [0, 24],
    popupAnchor: [2, -30],
    html: `<div>
            <span style="${makeMarkerHtmlStyles(color, opacity, selected, checked, shape, widthRem, heightRem)}"></span>
            <span class="${alarm ? 'alarm' : checked ? 'checked' : 'normal'}" style="${makeCenterHtmlStyles(alarm, checked, selected)}"></span>
            <div style="${divStyle}">${escapeHtml(name)}</div>
          </div>`,
  });
  return icon;
};

const CLUSTER_SIZE_PX = 36;

// Solid circle with centred count — used when multiple devices share a coordinate.
// Color reflects the worst status across the group (alarm > trace > active > category).
const makeClusterIcon = (color: string, count: number, opacity: number): Leaflet.DivIcon => {
  const half = CLUSTER_SIZE_PX / 2;
  return Leaflet.divIcon({
    className: 'lfndr-cluster-pin',
    iconAnchor: [half, half],
    popupAnchor: [0, -half],
    html: `<div style="
      width: ${CLUSTER_SIZE_PX}px;
      height: ${CLUSTER_SIZE_PX}px;
      background-color: ${color};
      opacity: ${opacity};
      border-radius: 50%;
      border: 2px solid rgba(0,0,0,0.25);
      box-shadow: 0 1px 4px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      font-weight: 700;
      color: white;
      text-shadow: 0 1px 2px rgba(0,0,0,0.4);
    ">${count}</div>`,
  });
};

const zoomFactorsPx: Record<number, number> = {
  18: 7,
  17: 3,
  16: 1.2,
  15: 0.4,
};

const makeAccuracyHtmlStyles = (
  color: string,
  opacity: number,
  selected: boolean,
  accuracy: number,
  size: number
): string => `
    background-color: ${color};
    opacity: ${opacity};
    width: ${'1rem'};
    height: ${'1rem'};
    display: block;
    border-radius: ${`${size + 0.5}rem ${size + 0.5}rem ${size + 0.5}rem ${size + 0.5}rem`};
    left: ${-size - 0.5 + 'rem'};
    top:  ${-size - 0.5 + 'rem'};
    position: relative;
    transform: rotate(45deg);
    border: ${size + 'rem'} solid ${'#aaa4'}`;

const makeAccuracyIcon = (
  color: string,
  opacity: number,
  selected: boolean,
  accuracy: number,
  zoom: number,
  showAccuracy: boolean
): Leaflet.DivIcon => {
  let effectiveAccuracy = accuracy;
  if (!(accuracy && showAccuracy)) effectiveAccuracy = 1;
  const size = zoomFactorsPx[zoom]
    ? (zoomFactorsPx[zoom] * effectiveAccuracy) / 100 /* 40 is accurate scale */
    : 0;
  const icon = Leaflet.divIcon({
    className: 'lfndr-custom-pin-' + zoom,
    iconAnchor: [size, size] as [number, number],
    popupAnchor: [0, -size],
    html: `<div>
                <span style="${makeAccuracyHtmlStyles(color, opacity, selected, effectiveAccuracy, size)}"></span>
              </div>`,
  });
  return icon;
};

const roundNdec = (x: number, k: number): number => {
  return Math.round(x * k) / k;
};

const formatShortDate = (date: Date): string =>
  date.toLocaleString('en', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

const DeviceMarker: React.FC<DeviceMarkerProps> = ({
  position,
  map,
  title,
  timestamp,
  opacity,
  positionTime,
  alarmTime,
  checkedTime,
  color,
  accuracy,
  point,
  selected,
  zindex,
  showAccuracy,
  shape,
  markerWidthRem,
  markerHeightRem,
}) => {
  const [refReady, setRefReady] = React.useState(false);
  const popupRef = React.useRef<Leaflet.Popup | null>(null);

  // Depend on the coordinate values, not the position array (fresh identity every
  // render): the popup should re-open when selection changes or the device moves,
  // not on every devices-poll re-render — that would resurrect a popup the user
  // closed while the device is still selected.
  const [posLat, posLng] = position;
  React.useEffect(() => {
    if (refReady && selected && popupRef.current) {
      popupRef.current.setLatLng([posLat, posLng]).openOn(map);
    }
  }, [selected, refReady, map, posLat, posLng]);

  return (
    <Marker
      key={point.id}
      position={position}
      icon={makeMarkerIcon(
        title,
        color,
        opacity,
        alarmTime ? true : false,
        checkedTime ? true : false,
        selected,
        shape,
        markerWidthRem,
        markerHeightRem
      )}
      zIndexOffset={zindex * 100 + 1000}
    >
      <Popup
        key={point.id}
        ref={(r) => {
          popupRef.current = r;
          setRefReady(true);
        }}
      >
        <div style={{ fontSize: headerSize, fontWeight: 600 }}>{title}</div>
        <hr />
        {alarmTime && <div>Alarm: {formatShortDate(alarmTime)}</div>}
        <div>Last position: {formatShortDate(positionTime)}</div>
        <div>Last signal: {formatShortDate(timestamp)}</div>
        {point.area && <div>Area: {point.area}</div>}
        {point.output && isFinite(point.output.temp ?? NaN) && (
          <div>Temperature: {roundNdec(point.output.temp!, 10)}°C</div>
        )}
        {point.output && isFinite(point.output.volts ?? NaN) && (
          <div>Voltage: {roundNdec(point.output.volts!, 10)}V</div>
        )}
        <hr />
        <div
          style={{ display: 'flex', justifyContent: 'center', gap: '0.5vmin', flexWrap: 'wrap' }}
        >
          {point.output && point.onAck && !point.output.alarmAck && !point.alarmAcknowledge && (
            <ClickOnceButton onClick={() => point.onAck!()} text="Acknowledge" />
          )}
          {point.onDis ? <ClickOnceButton onClick={() => point.onDis!()} text="Reject" /> : <div />}
        </div>
        {point.output && point.alarmAcknowledge ? (
          <div style={{ textAlign: 'center', fontSize: '1.8vmin', marginTop: '0.5vmin' }}>
            Alarm acknowledged by {point.alarmAcknowledge.username} at{' '}
            {new Date(point.alarmAcknowledge.timestamp).toLocaleString()}
          </div>
        ) : (
          <div />
        )}
        {point.output && point.output.alarmAck ? (
          <div style={{ fontWeight: 600, textAlign: 'center' }}>Alarm Acknowledged</div>
        ) : null}
        {/* Action buttons - centered */}
        <div
          style={{ display: 'flex', justifyContent: 'center', gap: '0.5vmin', flexWrap: 'wrap' }}
        >
          {accuracy && point.onSelect && (
            <ClickOnceButton onClick={() => point.onSelect!()} text="Trace" />
          )}

          {/* Tracking buttons for checker users */}
          {point.isChecker && !showAsTrace(point) && point.onBeginTracking && (
            <ClickOnceButton onClick={() => point.onBeginTracking!()} text="Begin Tracking" />
          )}
          {point.isChecker && showAsTrace(point) && point.onDeactivateTracking && (
            <ClickOnceButton
              onClick={() => point.onDeactivateTracking!()}
              text="Deactivate Tracking"
            />
          )}

          {/* Check-in buttons for checker users */}
          {point.isChecker && showAsChecked(point) && point.onUncheck && (
            <ClickOnceButton onClick={() => point.onUncheck!()} text="Uncheck" />
          )}
          {point.isChecker &&
            !showAsChecked(point) &&
            point.currentCheckpoint &&
            !point.checkpoint &&
            point.onCheckin && (
              <ClickOnceButton onClick={() => point.onCheckin!()} text="Check in" />
            )}

          {/* Checkpoint management buttons */}
          {point.isChecker &&
            point.checkpoint &&
            point.currentCheckpoint !== point.id &&
            point.onSetCurrentCheckpoint && (
              <ClickOnceButton
                onClick={() => point.onSetCurrentCheckpoint!(point.id)}
                text="Make Current"
              />
            )}
          {point.isChecker &&
            point.checkpoint &&
            point.currentCheckpoint === point.id &&
            point.onSetCurrentCheckpoint && (
              <ClickOnceButton
                onClick={() => point.onSetCurrentCheckpoint!(null)}
                text="Clear Current"
              />
            )}
          {point.isChecker && point.checkpoint && point.onClearAllCheckins && (
            <ClickOnceButton
              onClick={() => point.onClearAllCheckins!(point.id)}
              text="Clear all Checkins"
            />
          )}
        </div>
      </Popup>
    </Marker>
  );
};

const getDeviceColor = (point: DevicePoint, selected: string | null): string => {
  let color = getDeviceCategoryColor(point);
  if (showAsActive(point)) color = activeColor;
  if (showAsTrace(point)) color = traceColor;
  if (showAsAlarm(point)) color = alarmColor;
  if (point.id === selected) color = selectColor;
  return color;
};

interface StackedDeviceMarkerProps {
  position: [number, number];
  map: Leaflet.Map;
  color: string;
  opacity: number;
  stackedDevices: DevicePoint[];
  selected: string | null;
  zindex: number;
}

const StackedDeviceMarker: React.FC<StackedDeviceMarkerProps> = ({
  position,
  map,
  color,
  opacity,
  stackedDevices,
  selected,
  zindex,
}) => {
  const [refReady, setRefReady] = React.useState(false);
  const popupRef = React.useRef<Leaflet.Popup | null>(null);
  const isAnySelected = stackedDevices.some((d) => d.id === selected);

  // Same as DeviceMarker: key the effect on coordinate values, not array identity.
  const [posLat, posLng] = position;
  React.useEffect(() => {
    if (refReady && isAnySelected && popupRef.current) {
      popupRef.current.setLatLng([posLat, posLng]).openOn(map);
    }
  }, [isAnySelected, refReady, map, posLat, posLng]);

  const icon = makeClusterIcon(color, stackedDevices.length, opacity);

  return (
    <Marker position={position} icon={icon} zIndexOffset={zindex * 100 + 1000}>
      <Popup
        ref={(r) => {
          popupRef.current = r;
          setRefReady(true);
        }}
      >
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: '0.95em' }}>
          {stackedDevices.map((d) => (
            <li
              key={d.id}
              style={{
                color: getDeviceColor(d, null),
                fontWeight: d.id === selected ? 700 : 400,
                marginBottom: '0.2em',
              }}
            >
              {d.title || d.id}
            </li>
          ))}
        </ul>
      </Popup>
    </Marker>
  );
};

export const produceDeviceMarkers = (
  map: Leaflet.Map,
  series: DevicePoint[],
  selected: string | null,
  nowS: number,
  ageForMaxOpacityH: number,
  opacityScale: number,
  maxOpacity: number,
  showAccuracy: boolean
): React.ReactElement[] => {
  // Sort oldest first so newer devices render on top (higher z-index).
  const sortedSeries = [...series].sort((a, b) => {
    if (a.positionTime && b.positionTime)
      return new Date(a.positionTime).getTime() - new Date(b.positionTime).getTime();
    if (a.positionTime) return 1;
    else return -1;
  });

  // Group devices that share the exact same coordinate into one marker.
  const groups = new Map<string, DevicePoint[]>();
  for (const point of sortedSeries) {
    let lat = 0;
    let lng = 0;
    if (point.latlng && point.latlng.length === 2) {
      lat = point.latlng[0];
      lng = point.latlng[1];
    }
    const key = `${lat},${lng}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(point);
  }

  const result: React.ReactElement[] = [];
  let zindex = 0;

  for (const [coordKey, points] of groups) {
    const [lat, lng] = coordKey.split(',').map(Number);

    // Opacity based on the most-recently-positioned device in the group.
    const primary = points[points.length - 1];
    const ageS = primary.positionTime ? nowS - new Date(primary.positionTime).getTime() / 1000 : 0;
    const ageH = Math.min(ageS / 3600.0, ageForMaxOpacityH);
    const opacity = maxOpacity - (ageH / ageForMaxOpacityH) * opacityScale;

    if (points.length === 1) {
      const point = points[0];
      const color = getDeviceColor(point, selected);
      result.push(
        <DeviceMarker
          key={point.id}
          zindex={zindex}
          map={map}
          opacity={opacity}
          color={color}
          position={[lat, lng]}
          accuracy={point.accuracy}
          title={point.title}
          positionTime={new Date(point.positionTime)}
          alarmTime={point.alarmTime ? new Date(point.alarmTime) : undefined}
          checkedTime={point.checkinTime ? new Date(point.checkinTime) : undefined}
          timestamp={new Date(point.timestamp)}
          point={point}
          selected={point.id === selected}
          showAccuracy={showAccuracy}
          shape={getMarkerShape(point.deviceCategory)}
          markerWidthRem={point.markerWidthRem}
          markerHeightRem={point.markerHeightRem}
        />
      );
    } else {
      // Multiple devices at the same spot: single marker with count badge.
      // Colour reflects the worst status across all devices in the group.
      const isAnySelected = points.some((p) => p.id === selected);
      let stackColor: string;
      if (isAnySelected) {
        stackColor = selectColor;
      } else {
        stackColor = getDeviceCategoryColor(points[0]);
        if (points.some(showAsActive)) stackColor = activeColor;
        if (points.some(showAsTrace)) stackColor = traceColor;
        if (points.some(showAsAlarm)) stackColor = alarmColor;
      }
      result.push(
        <StackedDeviceMarker
          key={`stack-${lat}-${lng}`}
          position={[lat, lng]}
          map={map}
          color={stackColor}
          opacity={opacity}
          stackedDevices={points}
          selected={selected}
          zindex={zindex}
        />
      );
    }

    zindex++;
  }

  return result;
};

const SolutionMarker: React.FC<SolutionMarkerProps> = ({
  position,
  map,
  title,
  opacity,
  color,
  point,
  selected,
  zoom,
  zindex,
  showAccuracy,
}) => {
  const [refReady, setRefReady] = React.useState(false);
  const popupRef = React.useRef<Leaflet.Popup | null>(null);

  // Depend on the coordinate values, not the position array (fresh identity every
  // render): the popup should re-open when selection changes or the device moves,
  // not on every devices-poll re-render — that would resurrect a popup the user
  // closed while the device is still selected.
  const [posLat, posLng] = position;
  React.useEffect(() => {
    if (refReady && selected && popupRef.current) {
      popupRef.current.setLatLng([posLat, posLng]).openOn(map);
    }
  }, [selected, refReady, map, posLat, posLng]);

  return (
    <Marker
      key={point.id}
      position={position}
      zIndexOffset={zindex + 1000}
      icon={makeAccuracyIcon(
        color,
        opacity,
        selected,
        point.accuracy || 1,
        zoom,
        showAccuracy || false
      )}
    >
      <Popup
        key={point.id}
        ref={(r) => {
          popupRef.current = r;
          setRefReady(true);
        }}
      >
        <div style={{ fontSize: headerSize, fontWeight: 600 }}>{title}</div>
        <hr />
        Processed at:{' '}
        {point.addedTimestamp ? new Date(point.addedTimestamp).toLocaleString() : 'N/A'}
        <br />
        Accuracy: {point.accuracy} m
        <br />
        Status: {point.status}
      </Popup>
    </Marker>
  );
};

export const produceSolutionMarkers = (
  map: Leaflet.Map,
  series: SolutionPoint[],
  selected: string | null,
  nowS: number,
  ageForMaxOpacityH: number,
  opacityScale: number,
  maxOpacity: number,
  zoom: number,
  showAccuracy: boolean
): React.ReactElement[] => {
  return series.map((point, i) => {
    const ageS = point.timestamp ? nowS - new Date(point.timestamp).getTime() / 1000 : 0;
    let ageH = ageS / 3600.0;
    if (ageH > ageForMaxOpacityH) ageH = ageForMaxOpacityH;
    const agingFactor = (ageH / ageForMaxOpacityH) * opacityScale;
    const opacity = maxOpacity - agingFactor;
    const lat = point.lat;
    const lng = point.lng;

    const color = point.id === selected ? selectColor : deviceColor;

    return (
      <SolutionMarker
        key={i}
        map={map}
        opacity={opacity}
        color={color}
        position={[lat, lng]}
        showAccuracy={showAccuracy}
        title={point.title}
        point={point}
        selected={point.id === selected}
        zoom={zoom}
        zindex={i}
      />
    );
  });
};
